/**
 * The painted art (docs/UI.md, *Painted art*): a portrait for each of the cast, and the title illustration.
 * Small WebPs in public/art/ (built by scripts/build-art.mjs from art/prompts.json), loaded lazily: nothing
 * here is fetched before a page that shows it is drawn, and a missing or slow file never holds anything
 * up — the photo simply isn't pasted in.
 *
 * Pure lookups (`portraitId`, `portraitUrl`) are unit-tested; `pastePhoto` builds the DOM.
 */
import './art.css';

/** Everyone with a portrait, by id (the file name in public/art/portraits/). */
export const PORTRAIT_IDS = ['mags', 'halloran', 'sid', 'lenny', 'rita', 'dot', 'pike', 'wilf', 'terry', 'ronnie', 'clive', 'holloway', 'pinch', 'nobby', 'rudd'] as const;
export type PortraitId = (typeof PORTRAIT_IDS)[number];

/**
 * Who is who, by the names the game writes: a message's `from`, a job's `client`, a subtitle's speaker,
 * a contact's name. Matched case-insensitively on the name with any "(phone)" / "(radio)" tail taken off.
 */
const NAMES: Record<string, PortraitId> = {
  'mags delaney': 'mags',
  mags: 'mags',
  'des halloran': 'halloran',
  des: 'halloran',
  halloran: 'halloran',
  'halloran motors': 'halloran',
  'sid marlowe': 'sid',
  sid: 'sid',
  'lenny the ear': 'lenny',
  'lenny pruett': 'lenny',
  lenny: 'lenny',
  'rita vance': 'rita',
  rita: 'rita',
  'dot hennessy': 'dot',
  dot: 'dot',
  'kestrel cars': 'dot',
  'pike’s respray': 'pike',
  "pike's respray": 'pike',
  pike: 'pike',
  'wilf tuckett': 'wilf',
  wilf: 'wilf',
  'big terry': 'terry',
  'ronnie vane': 'ronnie',
  ronnie: 'ronnie',
  'clive vane': 'clive',
  clive: 'clive',
  'ds holloway': 'holloway',
  holloway: 'holloway',
  'verger pinch': 'pinch',
  'nobby clegg': 'nobby',
  nobby: 'nobby',
  'sgt. alf rudd': 'rudd',
  'alf rudd': 'rudd',
  rudd: 'rudd',
};

export function portraitId(name: string | undefined | null): PortraitId | null {
  if (!name) return null;
  const key = name
    .replace(/\s*\([^)]*\)\s*$/, '')
    .trim()
    .toLowerCase();
  return NAMES[key] ?? null;
}

/** Relative to the page (vite `base: './'`), so it works from any folder the build is served from. */
const BASE = import.meta.env?.BASE_URL ?? './';

export function portraitUrl(name: string | undefined | null): string | null {
  const id = portraitId(name);
  return id ? `${BASE}art/portraits/${id}.webp` : null;
}

/** The key art (scripts/build-splash.mjs): title screen, its small copy (loading screen), the portrait crop. */
export const TITLE_ART_URL = `${BASE}art/title.webp`;
export const TITLE_TOUCH_ART_URL = `${BASE}art/title-touch.webp`;
export const LOADING_ART_URL = `${BASE}art/loading.webp`;
export const PORTRAIT_ART_URL = `${BASE}art/title-portrait.webp`;

/** A small steady tilt per person (−2.6° … 2.6°), so the photos don't all sit at the same angle. */
export function tiltFor(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return ((Math.abs(h) % 11) - 5) * 0.52;
}

export type PhotoSize = 'thumb' | 'note' | 'large';

/**
 * A photo pasted into paper: a cream-bordered print, tilted a little, held by tape (`large`) or black
 * photo corners (`note`, `thumb`). Returns null (and adds nothing) when the name has no portrait. The image is lazy and
 * async-decoded; if it fails to load, the print takes itself away.
 */
export function pastePhoto(parent: HTMLElement, name: string, size: PhotoSize, before?: Node | null): HTMLElement | null {
  const url = portraitUrl(name);
  if (!url) return null;
  const fig = document.createElement('figure');
  fig.className = `bh-photo ${size}`;
  fig.dataset.portrait = portraitId(name)!;
  fig.style.setProperty('--tilt', `${tiltFor(name) * (size === 'thumb' ? 0.6 : 1)}deg`);
  fig.setAttribute('aria-hidden', 'true');
  const img = document.createElement('img');
  img.alt = '';
  img.decoding = 'async';
  img.loading = 'lazy';
  img.draggable = false;
  img.addEventListener('load', () => fig.classList.add('ready'), { once: true });
  img.addEventListener('error', () => fig.remove(), { once: true });
  img.src = url;
  fig.appendChild(img);
  if (before !== undefined) parent.insertBefore(fig, before);
  else parent.appendChild(fig);
  return fig;
}
